import { useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, MoreHorizontal } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, DeadlineKey, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, StatusBadge, SourceText } from "@ncp/ui";
import type { NextStep, WorkItem } from "./api";
import { stageOf } from "../case/process/phases";
import { PeriodRing } from "../../components/PeriodRing";

const PAGE = 5;

/** Files as rows: where each stands, who owns the stage, the nearest deadline, and the next step – the first as a button, the rest in a menu. */
export function WorkList({ items, empty, showAssignee = false }: { items: WorkItem[]; empty: string; showAssignee?: boolean }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return <p className="py-2 text-sm text-muted-foreground">{empty}</p>;
  const shown = expanded ? items : items.slice(0, PAGE);
  return (
    <div>
      <ul className="divide-y divide-sand-100">
        {shown.map((w) => <WorkRow key={w.caseId} item={w} showAssignee={showAssignee} />)}
      </ul>
      {shown.some((w) => w.primaryClock) ? <DeadlineKey className="mt-2" /> : null}
      {items.length > PAGE ? (
        <div className="border-t border-sand-100 pt-3">
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setExpanded((e) => !e)}>
            {expanded ? t("backoffice:commandCenter.showFewer") : t("backoffice:commandCenter.showAll", { count: items.length })}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function WorkRow({ item: w, showAssignee }: { item: WorkItem; showAssignee: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const steps = w.nextSteps.filter((s) => s.permitted);
  const [primary, ...others] = steps;
  const label = (s: NextStep) => (locale === "ar" && s.labelAr ? s.labelAr : s.label);
  const take = (s: NextStep) => navigate(`/cases/${w.caseId}?tab=review&step=${encodeURIComponent(s.trigger)}`);
  return (
    <li className="grid grid-cols-[8.5rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 py-3 first:pt-1">
      <div className="pt-0.5">
        {w.primaryClock ? <PeriodRing clock={w.primaryClock} size={22} /> : <span className="text-xs text-muted-foreground">{t("backoffice:cases.noPeriod")}</span>}
      </div>
      <div className="min-w-0">
        <button type="button" onClick={() => navigate(`/cases/${w.caseId}`)} className="block w-full min-w-0 text-start">
          <span className="line-clamp-2 text-sm font-medium text-aeblack-950 hover:text-aegold-800"><SourceText text={locale === "ar" && w.titleAr ? w.titleAr : w.title} /></span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            <bdi className="font-mono">{w.caseNumber}</bdi> · {t(`backoffice:workspace.phases.${stageOf(w.type, w.state, w.status)}`)}
          </span>
        </button>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {w.ownerRole ? <span>{t(`roles.${w.ownerRole}`, { defaultValue: w.ownerRole })}</span> : null}
          {showAssignee ? <><span aria-hidden>·</span><span className={w.assigneeName ? undefined : "text-warning-700"}>{(locale === "ar" && w.assigneeNameAr) || w.assigneeName || t("misc.unassigned")}</span></> : null}
          {w.priority === "High" || w.priority === "Critical" ? <StatusBadge status={w.priority} label={t(`enums.priority.${w.priority}`, { defaultValue: w.priority })} /> : null}
        </div>
        {primary ? (
          <div className="mt-2 flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7 min-w-0 max-w-full px-2.5 text-xs" onClick={() => take(primary)} title={label(primary)}>
              <span className="truncate">{label(primary)}</span> <ArrowRight className="shrink-0 rtl:rotate-180" />
            </Button>
            {others.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon-sm" variant="ghost" className="size-7 shrink-0" aria-label={t("backoffice:commandCenter.otherSteps", { count: others.length })}><MoreHorizontal /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-w-80">
                  {others.map((s) => <DropdownMenuItem key={s.trigger} onSelect={() => take(s)}><span className="truncate">{label(s)}</span></DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
